// System mapping: which of the model's categories each drawn system reads.
//
// The defaults fit ordinary Revit category names. A job that models its duct
// banks, cable trays or risers as something of its own says so here, either
// for the whole project or for one view (owner, 2026-09-17: "allow the
// specific edit of a system mapping like duct banks, cable trays, risers,
// based on the model, by adding edit for a specific view").
//
// The category list is what the live model actually contains, with element
// counts, so this is a pick list rather than a spelling test.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Empty, Input, InputNumber, Modal, Select, Skeleton, Table, Tag, Tooltip, message } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { glanceApi } from "./viewModel";
import { matchOption } from "components/SearchSelect/searchSelect";

const SYSTEM_LABEL = {
  telecom: "Telecom", power: "Power", lighting: "Lighting", sprinklers: "Sprinklers",
  ducts: "Ductwork", plumbing: "Plumbing", underfloor: "Below slab", roof: "Roof equipment",
};

export default function MappingEditor({ projectId, viewId, viewTitle, open, onClose, onSaved }) {
  const [state, setState] = useState(null);
  const [rules, setRules] = useState([]);
  const [weights, setWeights] = useState({});
  const [byType, setByType] = useState({});   // { "<type>": { <status>: 0..1, effort } } (guide 19.5)
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    glanceApi(projectId, "/system-map")
      .then((out) => { setState(out); setRules(out.rules || []); setWeights(out.statusWeights || {}); setByType(out.statusWeightsByType || {}); })
      .catch((e) => message.error(e.message));
  }, [projectId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const available = useMemo(() => (state && state.available) || [], [state]);
  const options = useMemo(() => {
    const seen = new Set(available.map((a) => a.category));
    const extra = [...new Set(Object.values((state && state.defaults) || {}).flat())].filter((c) => !seen.has(c));
    return [
      { label: "In this project's model", options: available.map((a) => ({ value: a.category, label: `${a.category} · ${a.elements} element${a.elements === 1 ? "" : "s"}${a.lengthFt ? `, ${a.lengthFt.toLocaleString()} ft` : ""}` })) },
      { label: "Not in this model", options: extra.map((c) => ({ value: c, label: c })) },
    ];
  }, [available, state]);

  const set = (i, patch) => setRules((rs) => rs.map((r, n) => (n === i ? { ...r, ...patch } : r)));
  const add = () => setRules((rs) => [...rs, { id: `r-${Date.now()}-${rs.length}`, system: "telecom", viewId: viewId || "", label: "", categories: [], exclude: [], typeMatch: "", note: "" }]);

  const save = async () => {
    setBusy(true);
    try {
      const out = await glanceApi(projectId, "/system-map", { method: "PUT", body: { rules, statusWeights: weights, statusWeightsByType: byType } });
      setRules(out.rules || []);
      setWeights(out.statusWeights || {});
      setByType(out.statusWeightsByType || {});
      message.success("Mapping saved. Compare again to use it.");
      if (onSaved) onSaved();
      onClose();
    } catch (e) { message.error(e.message); } finally { setBusy(false); }
  };

  const columns = [
    {
      title: "System", dataIndex: "system", width: 150,
      render: (v, r, i) => <Select value={v} style={{ width: 138 }} onChange={(x) => set(i, { system: x })}
                                   options={Object.keys(SYSTEM_LABEL).map((k) => ({ value: k, label: SYSTEM_LABEL[k] }))} />,
    },
    {
      title: "Applies to", dataIndex: "viewId", width: 190,
      render: (v, r, i) => <Select value={v || ""} style={{ width: 178 }} onChange={(x) => set(i, { viewId: x })}
                                   options={[{ value: "", label: "Every view on the project" }, ...(viewId ? [{ value: viewId, label: `Only ${viewTitle || viewId}` }] : [])]} />,
    },
    {
      title: "Model categories it reads", dataIndex: "categories",
      render: (v, r, i) => <Select mode="multiple" value={v} style={{ width: "100%", minWidth: 260 }} placeholder="Add a category from the model"
                                   onChange={(x) => set(i, { categories: x })} options={options} optionFilterProp="label" allowClear />,
    },
    {
      title: <Tooltip title="Categories the defaults would read, that this project should not">Leaves out</Tooltip>, dataIndex: "exclude",
      render: (v, r, i) => <Select mode="multiple" value={v} style={{ width: "100%", minWidth: 180 }} placeholder="Nothing"
                                   onChange={(x) => set(i, { exclude: x })} options={options} optionFilterProp="label" allowClear />,
    },
    {
      title: <Tooltip title="Optional: only model types whose name contains this, for a riser or a duct bank that lives inside a bigger category">Type contains</Tooltip>,
      dataIndex: "typeMatch", width: 150,
      render: (v, r, i) => <Input value={v} placeholder="riser" onChange={(e) => set(i, { typeMatch: e.target.value })} />,
    },
    {
      title: "Note", dataIndex: "note",
      render: (v, r, i) => <Input value={v} placeholder="Why this job is different" onChange={(e) => set(i, { note: e.target.value })} />,
    },
    {
      title: "", width: 44,
      render: (_, r, i) => <Button type="text" icon={<DeleteOutlined />} onClick={() => setRules((rs) => rs.filter((x, n) => n !== i))} />,
    },
  ];

  return (
    <Modal open={open} onCancel={onClose} width={1100} title="How systems map onto the model"
           okText="Save mapping" confirmLoading={busy} onOk={save}
           okButtonProps={{ disabled: !state || !state.canEdit }}>
      {!state ? <Skeleton active /> : (
        <>
          <p className="g-tsub">
            Reconciliation compares a system with the model categories listed here. Leave a system alone and it reads the defaults:
            {" "}{Object.entries(state.defaults || {}).slice(0, 3).map(([k, v]) => `${SYSTEM_LABEL[k] || k} → ${v.slice(0, 3).join(", ")}`).join("; ")}…
            {state.updatedByName ? ` Last changed by ${state.updatedByName}.` : ""}
          </p>
          {!available.length && (
            <div className="g-callout">This project has no model quantities yet, so the list below is the default category names rather than what the model holds.</div>
          )}
          {rules.length === 0
            ? <Empty description="No rules: every system reads the defaults" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            : <Table size="small" rowKey={(r, i) => r.id || i} columns={columns} dataSource={rules} pagination={false} />}
          <div style={{ marginTop: 20 }}>
            <b>What each field status is worth</b>
            <p className="g-tsub">
              What each field status counts for when progress is measured - on this project's views and on its schedule, which
              read the same numbers. Blocked and inspected are marks, not statuses, so they carry no weight of their own.
            </p>
            <div className="g-actions">
              {["projected", "procured", "being_installed", "installed"].map((k) => (
                <label key={k} className="g-tsub" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {k.replace(/_/g, " ")}
                  <InputNumber size="small" min={0} max={1} step={0.05} style={{ width: 90 }} disabled={!state.canEdit}
                               value={weights[k] != null ? weights[k] : (state.defaultWeights || {})[k]}
                               onChange={(v) => setWeights((w) => ({ ...w, [k]: v == null ? undefined : v }))} />
                </label>
              ))}
              <Button size="small" type="text" disabled={!state.canEdit} onClick={() => setWeights({})}>Back to the defaults</Button>
            </div>
            <p className="g-tsub" style={{ marginTop: 12 }}>
              By element type: a sleeve and a long homerun do not carry the same effort between installed and complete. A type's own
              numbers replace the ones above for its elements; effort says how much one of them counts against the others (1 = the same).
            </p>
            {Object.keys(byType).length > 0 && (
              <Table size="small" rowKey="type" pagination={false} style={{ marginBottom: 8 }}
                     dataSource={Object.entries(byType).map(([type, w]) => ({ type, w }))}
                     columns={[
                       { title: "Element type", dataIndex: "type" },
                       ...["projected", "procured", "being_installed", "installed"].map((k) => ({
                         title: k.replace(/_/g, " "), width: 96, render: (_, r) => (
                           <InputNumber size="small" min={0} max={1} step={0.05} style={{ width: 84 }} disabled={!state.canEdit} aria-label={`${r.type} ${k.replace(/_/g, " ")}`}
                                        placeholder={String(weights[k] != null ? weights[k] : (state.defaultWeights || {})[k])} value={r.w[k]}
                                        onChange={(v) => setByType((b) => ({ ...b, [r.type]: { ...b[r.type], [k]: v == null ? undefined : v } }))} />
                         ),
                       })),
                       { title: "effort", width: 90, render: (_, r) => (
                         <InputNumber size="small" min={0.1} max={1000} step={0.5} style={{ width: 80 }} disabled={!state.canEdit} aria-label={`${r.type} effort`}
                                      placeholder="1" value={r.w.effort} onChange={(v) => setByType((b) => ({ ...b, [r.type]: { ...b[r.type], effort: v == null ? undefined : v } }))} />
                       ) },
                       { title: "", width: 40, render: (_, r) => (
                         <Button size="small" type="text" icon={<DeleteOutlined />} aria-label={`Remove ${r.type}`} disabled={!state.canEdit}
                                 onClick={() => setByType((b) => { const next = { ...b }; delete next[r.type]; return next; })} />
                       ) },
                     ]} />
            )}
            <Select filterOption={matchOption} size="small" showSearch style={{ width: 320 }} placeholder="Add weights for an element type" value={undefined} disabled={!state.canEdit}
                    onChange={(t) => setByType((b) => (b[t] ? b : { ...b, [t]: {} }))}
                    options={[
                      { label: "Rows on this project's views", options: ((state.viewTypes || []).filter((t) => !byType[t.type])).map((t) => ({ value: t.type, label: `${t.type} · ${t.rows} row${t.rows === 1 ? "" : "s"}` })) },
                      { label: "Categories in the model", options: available.filter((a) => !byType[a.category]).map((a) => ({ value: a.category, label: `${a.category} · ${a.elements}` })) },
                    ]} />
          </div>

          <div style={{ marginTop: 12 }}>
            <Button icon={<PlusOutlined />} onClick={add} disabled={!state.canEdit}>Add a rule</Button>
            {available.length > 0 && (
              <span className="g-tsub" style={{ marginLeft: 12 }}>
                The model holds {available.length} categories, biggest first: {available.slice(0, 5).map((a) => <Tag key={a.category}>{a.category}</Tag>)}
              </span>
            )}
          </div>
        </>
      )}
    </Modal>
  );
}
